/**
 * Scoring Aggregation Pipeline
 * 
 * Aggregates MCF scores upward through the hierarchy:
 * MCF → MC (weighted) → Section (weighted) → Pillar (equal weight) → Overall (equal weight)
 * 
 * Key rules:
 * - Only scored (non-null) inputs participate in averages
 * - Weights are re-normalized among scored inputs so they sum to 1.0
 * - Pillars and overall use equal-weight averaging
 */

import { supabase } from '@/integrations/supabase/client';
import type { WeightedInput, AggregationResult } from './types';

export function weightedAverage(inputs: WeightedInput[]): AggregationResult {
  const scoredInputs = inputs.filter(i => i.score !== null && i.score !== undefined);

  if (scoredInputs.length === 0) {
    return {
      score: 0, inputCount: 0, totalPossible: inputs.length,
      coveragePercent: 0, normalizedWeightSum: 0, inputs: []
    };
  }

  const rawWeightSum = scoredInputs.reduce((sum, i) => sum + i.weight, 0);
  if (rawWeightSum === 0) {
    return {
      score: 0, inputCount: scoredInputs.length, totalPossible: inputs.length,
      coveragePercent: Math.round((scoredInputs.length / inputs.length) * 100),
      normalizedWeightSum: 0, inputs: scoredInputs
    };
  }

  const weightedSum = scoredInputs.reduce(
    (sum, i) => sum + ((i.score ?? 0) * (i.weight / rawWeightSum)),
    0
  );

  return {
    score: Math.round(weightedSum * 100) / 100,
    inputCount: scoredInputs.length,
    totalPossible: inputs.length,
    coveragePercent: Math.round((scoredInputs.length / inputs.length) * 100),
    normalizedWeightSum: 1.0,
    inputs: scoredInputs
  };
}

export function equalWeightAverage(scores: { entityId: string; score: number | null }[]): AggregationResult {
  const validScores = scores.filter(s => s.score !== null && s.score !== undefined);
  if (validScores.length === 0) {
    return {
      score: 0, inputCount: 0, totalPossible: scores.length,
      coveragePercent: 0, normalizedWeightSum: 0, inputs: []
    };
  }

  const equalWeight = 1 / validScores.length;
  const inputs: WeightedInput[] = validScores.map(s => ({
    entityId: s.entityId, score: s.score, weight: equalWeight
  }));

  const avg = validScores.reduce((sum, s) => sum + (s.score ?? 0), 0) / validScores.length;

  return {
    score: Math.round(avg * 100) / 100,
    inputCount: validScores.length,
    totalPossible: scores.length,
    coveragePercent: Math.round((validScores.length / scores.length) * 100),
    normalizedWeightSum: 1.0,
    inputs
  };
}

function groupBy<T>(arr: T[], key: keyof T): Record<string, T[]> {
  return arr.reduce((acc, item) => {
    const k = String(item[key]);
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {} as Record<string, T[]>);
}

export async function aggregateAllScores(
  mcfValueMap: Map<string, number>
): Promise<{
  mcScores: Map<string, AggregationResult>;
  sectionScores: Map<string, AggregationResult>;
  pillarScores: Map<string, AggregationResult>;
  overallScore: AggregationResult;
}> {
  // MCF → MC (weighted by mcf_weight)
  const { data: mcMcfMappingsData, error: mcMcfError } = await supabase
    .from('mc_mcf_mappings')
    .select('mc_id, mcf_id, mcf_weight');
  if (mcMcfError) {
    console.error('[aggregateAllScores] hierarchy query failed:', mcMcfError.message);
    throw new Error(`Score aggregation failed: ${mcMcfError.message}`);
  }
  const mcMcfMappings = mcMcfMappingsData ?? [];

  const mcGroups = groupBy(mcMcfMappings, 'mc_id' as any);
  const mcScores = new Map<string, AggregationResult>();

  for (const [mcId, mappings] of Object.entries(mcGroups)) {
    const inputs: WeightedInput[] = (mappings as any[]).map((m) => ({
      entityId: m.mcf_id,
      score: mcfValueMap.has(m.mcf_id) ? mcfValueMap.get(m.mcf_id)! : null,
      weight: Number(m.mcf_weight)
    }));
    const result = weightedAverage(inputs);
    if (result.inputCount > 0) {
      mcScores.set(mcId, result);
    }
  }

  // MC → Section (weighted by mc_weighting)
  const { data: mcsData, error: mcsError } = await supabase
    .from('measurement_components')
    .select('id, section_id, mc_weighting');
  if (mcsError) {
    console.error('[aggregateAllScores] hierarchy query failed:', mcsError.message);
    throw new Error(`Score aggregation failed: ${mcsError.message}`);
  }
  const mcs = mcsData ?? [];

  const sectionGroups = groupBy(mcs, 'section_id' as any);
  const sectionScores = new Map<string, AggregationResult>();

  for (const [sectionId, components] of Object.entries(sectionGroups)) {
    const inputs: WeightedInput[] = (components as any[]).map((mc) => ({
      entityId: mc.id,
      score: mcScores.has(mc.id) ? mcScores.get(mc.id)!.score : null,
      weight: Number(mc.mc_weighting)
    }));
    const result = weightedAverage(inputs);
    if (result.inputCount > 0) {
      sectionScores.set(sectionId, result);
    }
  }

  // Section → Pillar (equal weight among scored sections)
  const { data: sectionsData, error: sectionsError } = await supabase
    .from('scoring_sections')
    .select('id, pillar_id');
  if (sectionsError) {
    console.error('[aggregateAllScores] hierarchy query failed:', sectionsError.message);
    throw new Error(`Score aggregation failed: ${sectionsError.message}`);
  }
  const sections = sectionsData ?? [];

  const pillarGroups = groupBy(sections, 'pillar_id' as any);
  const pillarScores = new Map<string, AggregationResult>();

  for (const [pillarId, pillarSections] of Object.entries(pillarGroups)) {
    const scores = (pillarSections as any[]).map((s) => ({
      entityId: s.id,
      score: sectionScores.has(s.id) ? sectionScores.get(s.id)!.score : null
    }));
    const result = equalWeightAverage(scores);
    if (result.inputCount > 0) {
      pillarScores.set(pillarId, result);
    }
  }

  // Pillar → Overall (equal weight, each pillar 20%)
  const { data: pillarsData, error: pillarsError } = await supabase
    .from('scoring_pillars')
    .select('id');
  if (pillarsError) {
    console.error('[aggregateAllScores] hierarchy query failed:', pillarsError.message);
    throw new Error(`Score aggregation failed: ${pillarsError.message}`);
  }
  const pillars = pillarsData ?? [];

  console.log('[aggregateAllScores] hierarchy loaded:', {
    mcfMappings: mcMcfMappings.length,
    mcSections: mcs.length,
    sectionPillars: sections.length,
    pillars: pillars.length
  });

  const overallInputs = pillars.map((p) => ({
    entityId: p.id,
    score: pillarScores.has(p.id) ? pillarScores.get(p.id)!.score : null
  }));

  const overallScore = equalWeightAverage(overallInputs);

  return { mcScores, sectionScores, pillarScores, overallScore };
}
